import React from 'react';

const DividendInfo = ({ data }) => {
  if (!data || !data.dividends || data.dividends.length === 0) {
    return <div>No dividend data available for this stock</div>;
  }
  
  // Helper function to ensure a value is a number
  const ensureNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };
  
  // Helper function to safely format a number with fixed decimal places
  const safeToFixed = (value, decimals = 2) => {
    const num = ensureNumber(value);
    return num.toFixed(decimals);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Group dividends by year
  const groupDividendsByYear = () => {
    const groupedData = {};
    
    data.dividends.forEach(dividend => {
      const date = new Date(dividend.date);
      const year = date.getFullYear();
      
      if (!groupedData[year]) {
        groupedData[year] = [];
      }
      
      groupedData[year].push(dividend);
    });
    
    return groupedData;
  };
  
  const groupedDividends = groupDividendsByYear();
  const years = Object.keys(groupedDividends).sort((a, b) => b - a); // Sort years in descending order
  
  // Calculate annual dividend amounts
  const calculateAnnualDividends = () => {
    const annualData = {};
    
    years.forEach(year => {
      const dividendsInYear = groupedDividends[year];
      const totalAmount = dividendsInYear.reduce((sum, div) => sum + ensureNumber(div.amount), 0);
      
      annualData[year] = {
        count: dividendsInYear.length,
        totalAmount: totalAmount,
        dividends: dividendsInYear
      };
    });
    
    return annualData;
  };
  
  const annualDividends = calculateAnnualDividends();
  
  return (
    <div className="dividend-info">
      <div className="dividend-summary">
        <h3>Dividend Summary</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Current Price</span>
            <span className="summary-value">${safeToFixed(data.currentPrice)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Annual Dividend</span>
            <span className="summary-value">${safeToFixed(data.annualDividend)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Dividend Yield</span>
            <span className="summary-value">{data.dividendYieldPercentage || safeToFixed(ensureNumber(data.dividendYield) * 100) + '%'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Dividend Frequency</span>
            <span className="summary-value">{data.dividendFrequency || 'N/A'}</span>
          </div>
        </div>
      </div>
      
      <div className="dividend-history">
        <h3>Dividend History</h3>
        
        {years.map(year => (
          <div key={year} className="dividend-year-section">
            <h4>{year} - {annualDividends[year].count} Payments, Total: ${safeToFixed(annualDividends[year].totalAmount)}</h4>
            <table className="dividend-table">
              <thead>
                <tr>
                  <th>Ex-Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {annualDividends[year].dividends.map((dividend, index) => (
                  <tr key={index}>
                    <td>{formatDate(dividend.date)}</td>
                    <td>${safeToFixed(dividend.amount)}</td>
                    <td>{dividend.type || 'Regular'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DividendInfo;